import React, {useState} from "react";
import {Box, Button, MenuItem, TextField, Tooltip, Typography} from "@mui/material";

import {Blue3, Grey5, Grey6, Grey17} from "../../theme/styles";
import {ReportRun, SavedReport} from "./reportStoreApi";
import {BUILDER_COLUMN, BUILDER_SECOND_COLUMN} from "./builderLayout";

/**
 * Keeping a report, reopening one, and seeing what it said before.
 *
 * SAVING AND RUNNING ARE DIFFERENT ACTS, and the bar keeps them apart. A report is arranged and run
 * many times while somebody works out what they meant; only some of those arrangements are worth
 * keeping, and only some runs are worth keeping as evidence. Collapsing the two into one button
 * would either fill the history with drafts or make keeping a report a second thing to remember.
 *
 * THE HISTORY IS THE POINT OF THE PHASE, not a nicety. A report re-run next quarter gives different
 * numbers because the data moved, and "what did this say in March" has to have an answer. Each run
 * stores its own rows AND the specification that produced them, so a run still answers the question
 * it was asked even after the definition is edited.
 *
 * A RUN CANNOT BE DELETED FROM HERE, and there is no control for it because there is no endpoint
 * for it: the database grant does not permit it either. A report that HAS runs cannot be deleted
 * for the same reason -- the server refuses, and says how many.
 */

type Props = {
    reports: SavedReport[];
    current: SavedReport | null;
    name: string;
    dirty: boolean;
    runs: ReportRun[];
    busy: boolean;
    onNameChange: (name: string) => void;
    onOpen: (report: SavedReport | null) => void;
    onSave: () => void;
    onOpenRun: (run: ReportRun) => void;
};

export default function SavedReportBar(
        {reports, current, name, dirty, runs, busy, onNameChange, onOpen, onSave, onOpenRun}: Props) {
    const [showHistory, setShowHistory] = useState(false);

    return (
        <Box sx={{mb: 2.5}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap"}}>
                <TextField select size="small" label="Saved reports" sx={{width: BUILDER_COLUMN}}
                           value={current?.USID ?? ""}
                           onChange={event => onOpen(
                               reports.find(one => one.USID === event.target.value) ?? null)}>
                    {/* THE WAY BACK OUT. Without it, opening a saved report is a one-way door and
                        the only escape is reloading the page. */}
                    <MenuItem value="">— start a new one —</MenuItem>
                    {reports.map(one => (
                        <MenuItem key={one.USID} value={one.USID}>
                            {one.Name}{one.RunCount ? ` · ${one.RunCount} run${one.RunCount === 1 ? "" : "s"}` : ""}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField size="small" label="Name" sx={{width: BUILDER_SECOND_COLUMN}} value={name}
                           placeholder="Name it to keep it"
                           onChange={event => onNameChange(event.target.value)}/>

                <Tooltip title={current ? "Replace the saved definition" : "Keep this report"}>
                    <span>
                        <Button size="small" variant="outlined" disabled={busy || !name.trim()}
                                onClick={onSave}
                                sx={{textTransform: "none", fontSize: 13, borderColor: "#A8A9AF"}}>
                            {current ? "Save changes" : "Save report"}
                        </Button>
                    </span>
                </Tooltip>

                {/* SAID, NOT INFERRED FROM A DISABLED BUTTON. Somebody who has changed a saved
                    report and not saved it should be told, not left to notice. */}
                {current && dirty ? (
                    <Typography sx={{fontSize: 12.5, color: Grey6}}>unsaved changes</Typography>
                ) : null}

                {current ? (
                    <Typography onClick={() => setShowHistory(one => !one)}
                                sx={{fontSize: 12.5, color: Blue3, cursor: "pointer", ml: "auto"}}>
                        {showHistory ? "Hide history" : `History (${runs.length})`}
                    </Typography>
                ) : null}
            </Box>

            {current && showHistory ? (
                <Box sx={{mt: 1.5, pl: 0.5}}>
                    <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                                     textTransform: "uppercase", color: Blue3, mb: 0.5}}>
                        What this report said
                    </Typography>
                    <Typography sx={{fontSize: 12.5, color: Grey5, mb: 1, maxWidth: "90ch"}}>
                        Each run keeps its own rows and the specification that produced them, so a
                        run still answers the question it was asked even after the report is edited.
                        Runs cannot be edited or deleted.
                    </Typography>
                    {runs.length === 0 ? (
                        <Typography sx={{fontSize: 12.5, color: Grey6}}>
                            Not run yet. Running it will keep what it says.
                        </Typography>
                    ) : runs.map(run => (
                        <Box key={run.USID}
                             onClick={() => onOpenRun(run)}
                             sx={{display: "flex", gap: 2, alignItems: "baseline", py: 0.4,
                                  cursor: "pointer", "&:hover": {color: Blue3}}}>
                            <Typography sx={{fontSize: 13, color: Grey17, minWidth: 190}}>
                                {String(run.RunDatetime).slice(0, 19)}
                            </Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey6}}>
                                {run.RowCount} row{run.RowCount === 1 ? "" : "s"}
                                {run.Truncated ? " · capped" : ""}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ) : null}
        </Box>
    );
}
